import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import type { Restaurant } from '@/api/types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Spinner } from '@/components/ui/spinner'
import { assignmentsApi } from '@/api/endpoints/assignments'

interface BulkAssignModalProps {
  open: boolean
  onClose: () => void
  selectedSkus: Array<string>
  restaurants: Array<Restaurant>
  mode: 'assign' | 'unassign'
}

export function BulkAssignModal({
  open,
  onClose,
  selectedSkus,
  restaurants,
  mode,
}: BulkAssignModalProps) {
  const { t: tProducts } = useTranslation('products')
  const { t: tCommon } = useTranslation('common')
  const [selectedRestaurantIds, setSelectedRestaurantIds] = useState<
    Set<number>
  >(new Set())
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async () => {
      const results = await Promise.allSettled(
        selectedSkus.flatMap((sku) =>
          Array.from(selectedRestaurantIds).map(async (restaurantId) => {
            if (mode === 'assign') {
              return await assignmentsApi.assign(sku, restaurantId)
            } else {
              return await assignmentsApi.unassign(sku, restaurantId)
            }
          }),
        ),
      )

      const succeeded = results.filter((r) => r.status === 'fulfilled').length
      const failed = results.filter((r) => r.status === 'rejected').length

      return { succeeded, failed }
    },
    onSuccess: ({ succeeded, failed }) => {
      // Invalidate all assignment queries
      selectedSkus.forEach((sku) => {
        queryClient.invalidateQueries({ queryKey: ['assignments', sku] })
      })

      if (failed === 0) {
        toast.success(
          mode === 'assign'
            ? tProducts('bulkAssign.successAssign', {
                products: selectedSkus.length,
                restaurants: selectedRestaurantIds.size,
              })
            : tProducts('bulkAssign.successUnassign', {
                products: selectedSkus.length,
                restaurants: selectedRestaurantIds.size,
              }),
        )
      } else {
        toast.warning(
          mode === 'assign'
            ? tProducts('bulkAssign.partialAssign', { succeeded, failed })
            : tProducts('bulkAssign.partialUnassign', { succeeded, failed }),
        )
      }
      handleClose()
    },
    onError: () => {
      toast.error(
        mode === 'assign'
          ? tProducts('bulkAssign.failedAssign')
          : tProducts('bulkAssign.failedUnassign'),
      )
    },
  })

  const handleClose = () => {
    setSelectedRestaurantIds(new Set())
    onClose()
  }

  const toggleRestaurant = (restaurantId: number) => {
    const newSelection = new Set(selectedRestaurantIds)
    if (newSelection.has(restaurantId)) {
      newSelection.delete(restaurantId)
    } else {
      newSelection.add(restaurantId)
    }
    setSelectedRestaurantIds(newSelection)
  }

  const handleSubmit = () => {
    if (selectedRestaurantIds.size === 0) {
      toast.error(tProducts('bulkAssign.emptySelection'))
      return
    }
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'assign'
              ? tProducts('bulkAssign.titleAssign')
              : tProducts('bulkAssign.titleUnassign')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'assign'
              ? tProducts('bulkAssign.descriptionAssign', {
                  count: selectedSkus.length,
                })
              : tProducts('bulkAssign.descriptionUnassign', {
                  count: selectedSkus.length,
                })}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="h-[300px] pr-4">
          <div className="space-y-3">
            {restaurants.map((restaurant) => (
              <div key={restaurant.id} className="flex items-center space-x-2">
                <Checkbox
                  id={`restaurant-${restaurant.id}`}
                  checked={selectedRestaurantIds.has(restaurant.id)}
                  onCheckedChange={() => toggleRestaurant(restaurant.id)}
                />
                <Label
                  htmlFor={`restaurant-${restaurant.id}`}
                  className="text-sm font-normal cursor-pointer"
                >
                  {restaurant.name}
                </Label>
              </div>
            ))}
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={mutation.isPending}
          >
            {tCommon('cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={mutation.isPending}>
            {mutation.isPending && <Spinner className="w-4 h-4 mr-2" />}
            {mode === 'assign'
              ? tProducts('bulkAssign.submitAssign')
              : tProducts('bulkAssign.submitUnassign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
